import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isTicketRoute } from "@models/types";


/**
 * Caches browse query strings per ticket tab while the tickets shell stays mounted.
 * Used by tab navigation to restore filters and sort when switching between entities.
 * @returns getUrlParamsSearch - Returns the last cached search string for a browse pathname.
 */
export function useTicketBrowseUrlParamsCache() {

  const location = useLocation();
  const searchByPathRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const browsePathname = getBrowsePathname(location.pathname);

    if (!browsePathname) {
      return;
    }

    searchByPathRef.current[browsePathname] = location.search;
  }, [location.pathname, location.search]);

  /**
   * Returns the cached URL search string for a ticket browse tab.
   * @param pathname string - Browse pathname (e.g. `/customers`).
   * @returns Cached search string including the leading `?`, or an empty string when none is stored.
   */
  function getUrlParamsSearch(pathname: string): string {
    const normalizedPathname = normalizePathname(pathname);
    return searchByPathRef.current[normalizedPathname] ?? "";
  }

  return {
    getUrlParamsSearch
  };
}





//         *******************************************
//         *             helper functions            *
//         *******************************************



/**
 * Resolves a ticket browse pathname when the location is a top-level entity route.
 * @param pathname string - Current router pathname (e.g. `/customers`, `/items/add`).
 * @returns Normalized browse pathname (e.g. `/customers`) or null when not a browse tab route.
 */
function getBrowsePathname(pathname: string): string | null {
  
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length !== 1) {
    return null;
  }

  const [routeSegment] = segments;

  if (!isTicketRoute(routeSegment)) {
    return null;
  }

  return `/${routeSegment}`;
}


/**
 * Ensures a pathname has a leading slash for consistent cache lookups.
 * @param pathname string - Pathname with or without a leading slash.
 * @returns Pathname prefixed with `/`.
 */
function normalizePathname(pathname: string): string {
  
  if (pathname.startsWith("/")) {
    return pathname;
  }

  return `/${pathname}`;
}
